import { type FC, type RefObject } from 'react';

import { exhaustiveBorderStyle } from '../../util/borders';
import { getFullDimensions } from '../../util/dimensions';
import { mergeSx } from '../../util/mui/sx';
import { quantitativeSizeToString } from '../../util/sizes';
import { Box, type BoxProps } from '../structural/Box';

import * as constants from './constants';
import * as types from './types';

export interface AvatarWrapperProps
  extends types.AvatarStyleProps, Omit<BoxProps, keyof types.AvatarStyleProps> {
  readonly ref?: RefObject<HTMLDivElement>;
}

export const AvatarWrapper: FC<AvatarWrapperProps> = ({
  backgroundColor = constants.DefaultAvatarStyleProps.backgroundColor,
  borderColor = constants.DefaultAvatarStyleProps.borderColor,
  children,
  color = constants.DefaultAvatarStyleProps.color,
  fontSize,
  size = constants.DefaultAvatarStyleProps.size,
  ...props
}) => (
  <Box
    {...props}
    sx={mergeSx(
      t => ({
        ...exhaustiveBorderStyle(t, {
          color:
            typeof borderColor === 'function' ? borderColor(t) : (borderColor ?? 'transparent'),
          thickness: 1,
          useOutline: true,
        }),
        backgroundColor:
          typeof backgroundColor === 'function'
            ? backgroundColor(t)
            : (backgroundColor ?? undefined),
        borderRadius: '100%',
        color: typeof color === 'function' ? color(t) : color,
        fontSize:
          typeof fontSize === 'function'
            ? fontSize(t)
            : fontSize
              ? quantitativeSizeToString(fontSize, 'px')
              : constants.getAvatarFontSize(size),
        lineHeight: types.avatarSizeIsResponsive(size)
          ? constants.getAvatarFontSize(size)
          : quantitativeSizeToString(constants.getAvatarSize(size), 'px'),
        maxHeight: '100%',
        maxWidth: '100%',
      }),
      props.sx,
      {
        alignItems: 'center',
        aspectRatio: 1,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        justifyContent: 'center',
        overflow: 'hidden',
        ...(types.avatarSizeIsResponsive(size)
          ? getFullDimensions(size)
          : getFullDimensions({
              height: quantitativeSizeToString(constants.getAvatarSize(size), 'px'),
            })),
      },
    )}
  >
    {children}
  </Box>
);
